import { addLeadingSlash, asyncForEach, getComponent, loadAsyncComponent } from "./common";
import OrderMatrixLink from "./order-matrix-link";
import VariantSelector from "./variant-selector";
import ColorVariantPopupLink from "./color-variant-popup-link";
import ProductInquiryFormLink from "./product-inquiry-link";
import PriceMatrixLink from "./price-matrix-link";
import Cart from "./cart";
import ProductCount from "./product-count";
import ImageGallery from "./image-gallery";
import ProductAvailabilityTable from "./product-availability-table";
import ProductPrintingOptions from "./product-printing-options"
import StockDetailsPopupLink from "./stock-detail-popup-link";
import EnterCalculatorLink from "./enter-calculator-link"
import TronCalculator from "./tron-calculator";
import { ActionCreator, AjaxDispatcher } from "./ajax-dispatcher";
import $ from "jquery";
import PrintingOnlineDemand from './printing-online-demand';
import BuyProductLink from './buy-product-link';

let variantSelector: VariantSelector;

async function initOrderMatrixLink(): Promise<void> {
    const link = document.querySelector('#open-order-matrix-link') as HTMLAnchorElement;
    if (!link) {
        return;
    }
    const linkComponent = new OrderMatrixLink(link, addProductsToCart, openCalculator);
    await linkComponent.init();
}

function initImageGallery(): void {
    new ImageGallery('[data-fancybox="images"]', '#fancybox-more');
}

function initEnterCalculationLink(): void {
    const baseElement = document.querySelector('[data-component-part="enter-calculator-control"]') as HTMLElement;
    if (baseElement) {
        new EnterCalculatorLink(baseElement, openCalculator);
    }
}

function initPriceMatrixLink(): void {
    const link = document.querySelector('[data-component-part="open-price-matrix-link"]') as HTMLAnchorElement;
    if (!link) {
        return;
    }
    const linkComponent = new PriceMatrixLink(link);
    linkComponent.init();
}

function initVariantSelectors(): void {
    variantSelector = new VariantSelector(getProductDetailMainElement(), loadProductPage);
    variantSelector.init();
}

function initProductInquiryForm(): void {
    const linkElement = document.querySelector('[data-component-part="contact-us-link"]') as HTMLAnchorElement;
    if (!linkElement) {
        return;
    }
    const link = new ProductInquiryFormLink(linkElement);
    link.init();
}

function initColorVariantPopupLink(): void {
    const linkElement = document.querySelector('[data-component-part="all-color-variants-link"]') as HTMLElement;
    if (!linkElement) {
        return;
    }
    const link = new ColorVariantPopupLink(linkElement, loadProductPage);
    link.init();
}

function initStockDetailsPopupLink(): void {
    const linkElements = [...document.querySelectorAll('[data-component-part="stock-details-popup-link"]')];
    linkElements.forEach(element => {
        const linkElement = element as HTMLElement;
        linkElement && new StockDetailsPopupLink(element as HTMLElement);
    });
}

async function initProductDetailOrderControls(): Promise<void>{
    const element = document.querySelector('[data-component="ProductDetailOrderControls"]') as HTMLElement;
    if(!element){
        return;
    }
    
    if (element.dataset.loadAsync) {
        await loadAsyncComponent(element);
    }

    //All prices popup for all model-variants
    initPriceMatrixLink(); 
    
    //order control buttons for model-variants (buy without or with printing)
    await initOrderMatrixLink();
    initEnterCalculationLink();

    let detailMainElement= getProductDetailMainElement();
    if(!detailMainElement) {
        throw 'ProductDetailMain not found';
    }
    
    //for single product?
    new BuyProductLink(detailMainElement); //quantity edit inputs (increment/decrement ...)
    
    //Demand for both product types
    new PrintingOnlineDemand(detailMainElement);
}

async function initStockInformation(): Promise<void> {
    const element = document.querySelector('[data-component="ProductDetailStockInfo"]') as HTMLElement;
    if (element && element.dataset.loadAsync) {
        await loadAsyncComponent(element);
    }
}

async function initStockTable(): Promise<void> {
    const elements = document.querySelectorAll('[data-component="ProductDetailAvailabilityTable"]');
    await asyncForEach([...elements], e => new ProductAvailabilityTable(e as HTMLElement).load());
}

function initProductPrintingOptions() {
    const optionsElement = getComponent(getProductDetailElement(), ProductPrintingOptions.componentName);
    optionsElement && new ProductPrintingOptions(optionsElement).load();
}


function loadProductPage(pageUrl: string, addToHistory = true): Promise<void> {
    const url = addLeadingSlash(pageUrl);

    const action = new ActionCreator().reloadComponents(['ProductDetailMain', 'ProductDetailData', 'Breadcrumbs'], url);
    action.successAction = async () => {
        if (addToHistory) {
            window.history.pushState(new ProductPageState(getProductId()), '', url);
        }
        await initProductDetail();
    };

    new AjaxDispatcher().dispatchGetAction(action);
    return Promise.resolve();
}

function initProductDetailData() {
    $('.toggle-tabs-init ul li span:first').addClass('selected');
    $('.toggle-tabs-init .tab-content-wrapper').hide();
    $('.toggle-tabs-init .tab-content-wrapper:first').show();

    $('.toggle-tabs-init .tabs-nav__button').click(function () {
        var t = $(this).attr('id');
        $('.toggle-tabs-init .tabs-nav__button').removeClass('selected');
        $('.toggle-tabs-init .tab-content-wrapper').hide();
        $(this).addClass('selected');
        $('#' + t + 'C').fadeIn('slow');
    });
}

function getProductDetailElement(): HTMLElement {
    return getComponent(document.body, 'ProductDetail');
}

function getProductDetailMainElement(): HTMLElement {
    return getComponent(document.body, 'ProductDetailMain');
}

export const getProductId = () => Number(getProductDetailMainElement().dataset.productId);
export const getParentId = () => Number(getProductDetailMainElement().dataset.parentId);

export const tryGetProductId = () => {
    const idString = getProductDetailMainElement()?.dataset.productId;
    return parseInt(idString);
};

export const getProductModelGuid = () => getProductDetailMainElement().dataset.modelGuid;
export const getProductGuid = () => getProductDetailMainElement().dataset.productGuid;

export const getProductSupplierGuid = () => getProductDetailMainElement().dataset.supplierGuid;
export const getProductDimension1 = () => getProductDetailMainElement().dataset.dim1;
export const getProductDimension2 = () => getProductDetailMainElement().dataset.dim2;

async function addProductsToCart(productCounts: ProductCount[]): Promise<void> {
    const cart = new Cart();
    await cart.addProduct(productCounts);
    await cart.openCartDialog(productCounts);
}

export async function openCalculator(productCounts: ProductCount[], positionId: string = null, technologyId: string = null): Promise<void> {
    const calculator = new TronCalculator();
    if (productCounts && productCounts.length > 0) {
        await calculator.openFromMatrix(getParentId(), getProductModelGuid(), getProductSupplierGuid(), productCounts, technologyId, positionId);
    } else {
        await calculator.openForProduct(getParentId(), getProductModelGuid(), getProductSupplierGuid(), getProductDimension1(), getProductDimension2())
    }
}

export async function initProductDetail() {
    initImageGallery();
    initVariantSelectors();
    initColorVariantPopupLink();
    await initStockTable();
    await initProductDetailOrderControls();
    await initStockInformation();
    initProductInquiryForm();
    initStockDetailsPopupLink();
    initProductPrintingOptions();
    initProductDetailData();
}

export async function loadProductVariant(productPageState: ProductPageState) : Promise<void> {
    if (!variantSelector || !productPageState || productPageState.productId === getProductId()) {
        return;
    }
    await loadProductPage(window.location.pathname, false);
}

export class ProductPageState {
    productId: number;
    constructor(productId) {
        this.productId = productId;
    }
}





